import { useEffect, useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, PaperPlaneTilt, Plus, Trash, DownloadSimple, ClockCounterClockwise, ArrowClockwise, ChatCircleDots, Prohibit } from "@phosphor-icons/react";

const FIELD_GROUPS = [
  { title: "Seller (our client)", fields: [["seller_names", "Seller name(s)"], ["seller_address", "Seller address"]] },
  { title: "Buyer (other side)", fields: [["buyer_names", "Buyer name(s)"], ["buyer_address", "Buyer address"]] },
  { title: "Property & title", fields: [["property_address", "Property address"], ["title_number", "Title number"], ["title_guarantee", "Title guarantee"], ["incumbrances", "Incumbrances"]] },
  { title: "Financials & completion", fields: [["purchase_price", "Purchase price (£)"], ["deposit", "Deposit (£)"], ["completion_date", "Completion date"], ["contract_rate", "Contract rate"], ["exchange_method", "Exchange method (A/B/C)"]] },
  { title: "Conveyancers", fields: [["seller_conveyancer", "Seller's conveyancer (us)"], ["seller_conveyancer_address", "Our address"], ["seller_solicitor_name", "Acting solicitor (us)"], ["seller_solicitor_email", "Acting solicitor email"], ["buyer_conveyancer", "Buyer's conveyancer"], ["buyer_conveyancer_address", "Their address"], ["buyer_conveyancer_ref", "Their reference"]] },
];

export default function ContractPackDialog({ caseId, open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState({});
  const [stdConds, setStdConds] = useState([]);
  const [selectedConds, setSelectedConds] = useState({});
  const [extraConds, setExtraConds] = useState([]);
  const [docs, setDocs] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState({});
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [alsoPdf, setAlsoPdf] = useState(true);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [replyView, setReplyView] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);

  const handleViewReply = async (reply) => {
    if (!reply?.graph_message_id) {
      setReplyView({ subject: reply?.subject, from: reply?.from, body_preview: reply?.preview, body_html: "" });
      return;
    }
    setReplyLoading(true);
    setReplyView({ loading: true });
    try {
      const { data } = await API.get(`/cases/${caseId}/contract-pack/reply`, { params: { graph_message_id: reply.graph_message_id } });
      setReplyView(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reply");
      setReplyView({ subject: reply?.subject, from: reply?.from, body_preview: reply?.preview, body_html: "" });
    } finally {
      setReplyLoading(false);
    }
  };

  const loadLog = () =>
    API.get(`/cases/${caseId}/contract-pack/log`)
      .then(({ data }) => setLog(data.entries || []))
      .catch(() => {});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    API.get(`/cases/${caseId}/contract-pack/defaults`)
      .then(({ data }) => {
        setFields(data.fields || {});
        setStdConds(data.special_conditions || []);
        const fd = data.firm_defaults || {};
        const defaultKeys = (fd.special_condition_keys && fd.special_condition_keys.length)
          ? fd.special_condition_keys
          : ["vacant_possession"];
        const sc = {};
        (data.special_conditions || []).forEach((c) => { sc[c.key] = defaultKeys.includes(c.key); });
        setSelectedConds(sc);
        setDocs(data.documents || []);
        const pre = {};
        (fd.preselect_document_ids || []).forEach((id) => { pre[id] = true; });
        setSelectedDocs(pre);
        setAlsoPdf(fd.also_pdf !== undefined ? !!fd.also_pdf : true);
        setExtraConds([]);
        setToEmail(data.to_email || "");
        setSubject(`Draft Contract — ${(data.fields || {}).property_address || "property"}`);
        setMessage("");
      })
      .catch(() => toast.error("Could not load contract details"))
      .finally(() => setLoading(false));
    loadLog();
  }, [open, caseId]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const condKeys = () => Object.keys(selectedConds).filter((k) => selectedConds[k]);
  const docIds = () => Object.keys(selectedDocs).filter((k) => selectedDocs[k]);
  const cleanExtras = () => extraConds.map((e) => e.trim()).filter(Boolean);

  const payload = () => ({
    fields,
    special_condition_keys: condKeys(),
    extra_conditions: cleanExtras(),
    also_attach_pdf: alsoPdf,
  });

  const handleGenerate = async () => {
    setBusy(true);
    const tid = toast.loading("Generating contract…");
    try {
      const { data } = await API.post(`/cases/${caseId}/contract-pack/generate`, payload());
      toast.success(`Contract generated & filed to Documents › Contract Pack${data.pdf_document_id ? " (DOCX + PDF)" : ""}`, { id: tid });
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${data.pdf_document_id || data.document_id}/download`, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!toEmail.trim()) return toast.error("Add the buyer's solicitor email to send");
    setBusy(true);
    const tid = toast.loading("Sending contract pack…");
    try {
      const { data } = await API.post(`/cases/${caseId}/contract-pack/send`, {
        ...payload(),
        to_email: toEmail.trim(),
        subject,
        message: message ? `<p>${message.replace(/\n/g, "<br/>")}</p>` : null,
        attachment_document_ids: docIds(),
      });
      toast.success(`Sent to ${data.to} with ${data.attachments} attachment(s)${data.mode === "mock" ? " (email in test mode)" : ""}`, { id: tid });
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async (entryId) => {
    setResendingId(entryId);
    const tid = toast.loading("Resending contract pack…");
    try {
      const { data } = await API.post(`/cases/${caseId}/contract-pack/resend/${entryId}`);
      toast.success(`Resent to ${data.to} with ${data.attachments} attachment(s)${data.mode === "mock" ? " (email in test mode)" : ""}`, { id: tid });
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend", { id: tid });
    } finally {
      setResendingId(null);
    }
  };

  const handleVoid = async (entryId) => {
    const note = window.prompt("Mark this send as an error. Add an optional note:", "Sent in error");
    if (note === null) return;
    try {
      await API.post(`/cases/${caseId}/contract-pack/log/${entryId}/void`, { note });
      toast.success("Entry marked as voided");
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to void");
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Permanently remove this send-history line? This cannot be undone.")) return;
    try {
      await API.delete(`/cases/${caseId}/contract-pack/log/${entryId}`);
      toast.success("Entry removed");
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const fmtTs = (ts) => {
    try { return new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ts; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText weight="bold" className="w-5 h-5 text-[#1462a0]" /> Contract Pack</DialogTitle>
          <DialogDescription>
            Draft the Contract (Standard Conditions of Sale, 5th ed.) from case data — edit anything, add special conditions, attach case documents, and email it to the buyer's solicitor.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading case details…</div>
        ) : (
          <div className="space-y-5">
            {FIELD_GROUPS.map((g) => (
              <div key={g.title} className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">{g.title}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {g.fields.map(([key, label]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{label}</Label>
                      <Input
                        value={fields[key] || ""}
                        onChange={(e) => setField(key, e.target.value)}
                        className="h-9 text-sm"
                        data-testid={`contract-field-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Special conditions */}
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">Special conditions (last page)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {stdConds.map((c) => (
                  <label key={c.key} className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`contract-cond-${c.key}`}>
                    <Checkbox
                      checked={!!selectedConds[c.key]}
                      onCheckedChange={(v) => setSelectedConds((s) => ({ ...s, [c.key]: !!v }))}
                      data-testid={`contract-cond-check-${c.key}`}
                    />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-2 pt-1">
                {extraConds.map((val, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      value={val}
                      onChange={(e) => setExtraConds((a) => a.map((x, j) => (j === i ? e.target.value : x)))}
                      placeholder="Add your own special condition…"
                      className="h-9 text-sm"
                      data-testid={`contract-extra-cond-${i}`}
                    />
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-rose-600" onClick={() => setExtraConds((a) => a.filter((_, j) => j !== i))} data-testid={`contract-extra-cond-remove-${i}`}>
                      <Trash weight="bold" className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setExtraConds((a) => [...a, ""])} data-testid="contract-add-condition">
                  <Plus weight="bold" className="w-3.5 h-3.5 mr-1" /> Add special condition
                </Button>
              </div>
            </div>

            {/* Attachments */}
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">Attach case documents ({docIds().length} selected)</div>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1">
                {docs.length === 0 && <p className="text-xs text-slate-400">No documents on this case yet.</p>}
                {docs.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`contract-doc-${d.id.slice(0, 8)}`}>
                    <Checkbox checked={!!selectedDocs[d.id]} onCheckedChange={(v) => setSelectedDocs((s) => ({ ...s, [d.id]: !!v }))} />
                    <span className="truncate">{d.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">Email to the buyer's solicitor</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">To (auto-filled from Other Parties)</Label>
                  <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="solicitor@firm.co.uk" className="h-9 text-sm" data-testid="contract-to-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 text-sm" data-testid="contract-subject" />
                </div>
              </div>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional covering message (a professional default is used if left blank)…" className="text-sm min-h-[70px]" data-testid="contract-message" />
            </div>

            {/* Send history */}
            {log.length > 0 && (
              <div className="space-y-2 border-t border-slate-200 pt-4" data-testid="contract-log">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">
                  <ClockCounterClockwise weight="bold" className="w-3.5 h-3.5" /> Send history ({log.length})
                </div>
                <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {log.map((e) => (
                    <div key={e.id} className={`flex items-center justify-between gap-2 px-3 py-2 ${e.voided ? "bg-rose-50/60" : ""}`} data-testid={`contract-log-entry-${e.id}`}>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-slate-800 truncate flex items-center gap-1.5">
                          <span className={e.voided ? "line-through text-slate-400" : ""}>
                            {e.type === "contract_pack_resent" ? "Resent" : "Sent"} to {e.to}
                          </span>
                          {e.voided && <span className="text-[10px] font-semibold uppercase text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded">Voided</span>}
                          {e.mode === "mock" && <span className="text-[10px] text-amber-600">(test mode)</span>}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {fmtTs(e.ts)} · by {e.by_name || "—"} · {e.attachments} attachment{e.attachments === 1 ? "" : "s"}{e.also_pdf ? " · PDF" : ""}
                          {e.receipt && (
                            <span className={`ml-1.5 font-medium ${e.receipt.status === "undeliverable" ? "text-rose-600" : "text-sky-700"}`} data-testid={`contract-log-receipt-${e.id}`}>
                              · {e.receipt.status === "read" ? "Read" : e.receipt.status === "not_read" ? "Not read" : e.receipt.status === "undeliverable" ? "Undeliverable" : "Delivered"} {fmtTs(e.receipt.at)}
                            </span>
                          )}
                        </div>
                        {e.reply && (
                          <button
                            type="button"
                            onClick={() => handleViewReply(e.reply)}
                            className="text-[11px] text-emerald-700 font-medium flex items-center gap-1 mt-0.5 hover:underline"
                            data-testid={`contract-log-reply-${e.id}`}
                          >
                            <ChatCircleDots weight="fill" className="w-3 h-3" /> Reply received {fmtTs(e.reply.replied_at)}{e.reply.from ? ` from ${e.reply.from}` : ""} — View
                          </button>
                        )}
                        {e.voided && e.void_note && (
                          <div className="text-[11px] text-rose-600 mt-0.5 truncate">⚠ {e.void_note}{e.voided_by ? ` — ${e.voided_by}` : ""}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button" variant="outline" size="sm"
                          className="h-8 text-xs"
                          disabled={resendingId === e.id}
                          onClick={() => handleResend(e.id)}
                          data-testid={`contract-resend-${e.id}`}
                        >
                          <ArrowClockwise weight="bold" className="w-3.5 h-3.5 mr-1" />
                          {resendingId === e.id ? "Resending…" : "Resend"}
                        </Button>
                        {!e.voided && (
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-amber-600" title="Mark as sent in error" onClick={() => handleVoid(e.id)} data-testid={`contract-void-${e.id}`}>
                            <Prohibit weight="bold" className="w-4 h-4" />
                          </Button>
                        )}
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-600" title="Remove line" onClick={() => handleDeleteEntry(e.id)} data-testid={`contract-delentry-${e.id}`}>
                          <Trash weight="bold" className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-end items-center gap-3 border-t border-slate-200 pt-4 sticky bottom-0 bg-white">
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer mr-auto" data-testid="contract-pdf-toggle">
                <Checkbox checked={alsoPdf} onCheckedChange={(v) => setAlsoPdf(!!v)} data-testid="contract-pdf-check" />
                <span>Also attach as PDF (locked copy)</span>
              </label>
              <Button type="button" variant="outline" onClick={handleGenerate} disabled={busy} data-testid="contract-generate-btn">
                <DownloadSimple weight="bold" className="w-4 h-4 mr-1.5" /> Generate DOCX
              </Button>
              <Button type="button" onClick={handleSend} disabled={busy} className="bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="contract-send-btn">
                <PaperPlaneTilt weight="bold" className="w-4 h-4 mr-1.5" /> {busy ? "Working…" : "Email to other side"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Reply preview — read the buyer-side reply without leaving the case */}
      <Dialog open={!!replyView} onOpenChange={(v) => !v && setReplyView(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="contract-reply-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{replyView?.subject || "Reply"}</DialogTitle>
            <DialogDescription>
              {replyView?.from ? `From ${replyView.from}` : ""}{replyView?.sent_at ? ` · ${fmtTs(replyView.sent_at)}` : ""}
            </DialogDescription>
          </DialogHeader>
          {replyLoading || replyView?.loading ? (
            <div className="text-sm text-slate-500 py-6 text-center">Loading reply…</div>
          ) : replyView?.body_html ? (
            <div className="prose prose-sm max-w-none text-sm text-slate-800" data-testid="contract-reply-body" dangerouslySetInnerHTML={{ __html: replyView.body_html }} />
          ) : (
            <div className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="contract-reply-body">
              {replyView?.body_preview || "No preview available."}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
